/**
 * Configuration constants for QA Automated Tests plugin
 * Based on qa-self-service reference implementation
 */

// Business Units configuration
export interface BusinessUnit {
  id: string;
  label: string;
  country: string;
  bu: string;
  icon: string;
}

export const BUSINESS_UNITS: BusinessUnit[] = [
  {
    id: 'bf-mx',
    label: 'Ben & Frank MX',
    country: 'mx',
    bu: 'benandfrank',
    icon: '🇲🇽',
  },
  {
    id: 'bf-cl',
    label: 'Ben & Frank CL',
    country: 'cl',
    bu: 'benandfrank',
    icon: '🇨🇱',
  },
  {
    id: 'bf-co',
    label: 'Ben & Frank CO',
    country: 'co',
    bu: 'benandfrank',
    icon: '🇨🇴',
  },
  {
    id: 'bv-mx',
    label: 'Bombavista MX',
    country: 'mx',
    bu: 'bombavista',
    icon: '💣',
  },
];

// Environment configuration
export type Environment = 'dev' | 'staging' | 'prod';

export const ENVIRONMENTS: Record<Environment, string> = {
  dev: 'Development',
  staging: 'Staging',
  prod: 'Production',
};

// Website/Application configuration
export type Website = 'odoo' | 'ecommerce';

export const WEBSITES: Record<Website, string> = {
  odoo: 'Odoo',
  ecommerce: 'E-Commerce',
};

// URL matrix by website, environment, and business unit
export const URLS: Record<Website, Record<Environment, Record<string, string>>> = {
  odoo: {
    staging: {
      'bf-mx': 'https://templos-mx.staging.casimiros.com/web/login',
      'bf-cl': 'https://templos-cl.staging.casimiros.com/web/login',
      'bf-co': 'https://templos-co.staging.casimiros.com/web/login',
      'bv-mx': 'https://tiendas-mx.staging.casimiros.com/web/login',
    },
    prod: {
      'bf-mx': 'https://templos-mx.casimiros.com/web/login',
      'bf-cl': 'https://templos-cl.casimiros.com/web/login',
      'bf-co': 'https://templos-co.casimiros.com/web/login',
      'bv-mx': 'https://tiendas-mx.casimiros.com/web/login',
    },
    dev: {
      'bf-mx': 'https://templos-mx.dev.casimiros.com/web/login',
      'bf-cl': 'https://templos-cl.dev.casimiros.com/web/login',
      'bf-co': 'https://templos-co.dev.casimiros.com/web/login',
      'bv-mx': 'https://tiendas-mx.dev.casimiros.com/web/login',
    },
  },
  ecommerce: {
    staging: {
      'bf-mx': 'https://staging.benandfrank.com/',
      'bf-cl': 'https://staging.benandfrank.cl/',
      'bf-co': 'https://staging.benandfrank.co/',
      'bv-mx': 'https://staging.bombavista.mx/',
    },
    prod: {
      'bf-mx': 'https://www.benandfrank.com/',
      'bf-cl': 'https://www.benandfrank.cl/',
      'bf-co': 'https://www.benandfrank.co/',
      'bv-mx': 'https://www.bombavista.mx/',
    },
    dev: {
      'bf-mx': 'https://dev.benandfrank.com/',
      'bf-cl': 'https://dev.benandfrank.cl/',
      'bf-co': 'https://dev.benandfrank.co/',
      'bv-mx': 'https://dev.bombavista.mx/',
    },
  },
};

// Test tag configuration
export interface TagOption {
  v: string;  // Value
  l: string;  // Label
}

// Special feature module for Odoo Production (matches DoB's PROD_ODOO_FEATURE)
export const PROD_ODOO_FEATURE = 'smokeprod/*';
export const UNAVAILABLE_FEATURE = 'unavailable';

export const TAGS: Record<Website, TagOption[]> = {
  odoo: [
    { v: 'customer', l: 'Customer Journey' },
    { v: 'fullfillment', l: 'Fulfillment & Lifecycle' },
    { v: 'enterprise', l: 'Enterprise Systems Enablement' },
    { v: 'foundational', l: 'Foundational Services' },
  ],
  ecommerce: [
    { v: 'customer_campaign', l: 'Campaigns & Brand Awareness' },
    { v: 'customer_product', l: 'Product Exploration' },
    { v: 'customer_purchase', l: 'Purchase Flow' },
    { v: 'customer_appointment', l: 'Appointments & Stores' },
  ],
};

// Helper function to get URL
export function getUrl(website: Website, env: Environment, buId: string): string | undefined {
  return URLS[website]?.[env]?.[buId];
}

// Helper function to check if feature is allowed in production (DoB-compatible)
export function isFeatureAllowedInProduction(
  website: Website,
  env: Environment,
  feature: string,
): boolean {
  // Odoo Production only allows smokeprod/*
  if (website === 'odoo' && env === 'prod') {
    return feature === PROD_ODOO_FEATURE;
  }
  // All other environments and websites allow all features
  return true;
}

// Helper function to get available tags for website
export function getTagsForWebsite(website: Website): TagOption[] {
  return TAGS[website] || [];
}

// Helper function to get business unit by ID
export function getBusinessUnitById(id: string): BusinessUnit | undefined {
  return BUSINESS_UNITS.find(bu => bu.id === id);
}

// Feature module configuration based on qa-self-service COVERAGE
export interface FeatureModule {
  f: string;  // Feature file name
  n: string;  // Feature display name
  c: number;  // Case count (placeholder - would be populated from actual test data)
}

export interface CoverageData {
  [module: string]: FeatureModule[];
}

export type CoverageByEnvironment = Record<Environment, Record<string, CoverageData>>;

// COVERAGE structure organized by website → environment → buId → feature modules
// Based on qa-self-service repository structure
export const COVERAGE: Record<Website, CoverageByEnvironment> = {
  odoo: {
    staging: {
      'bf-mx': {
        'login': [
          { f: 'login.feature', n: 'Login', c: 1 }
        ],
        'calendar_flow': [
          { f: 'calendar_flow.feature', n: 'Calendar configuration MX', c: 1 }
        ],
        'checkout': [
          { f: 'checkout_express_delivery_killswitch.feature', n: 'Checkout express-delivery kill switch', c: 1 }
        ],
        'contacts_flow': [
          { f: 'contacts_flow.feature', n: 'Contacts Flow MX', c: 1 }
        ],
        'draftorder': [
          { f: 'draftorder.feature', n: 'Draftorder MX', c: 1 }
        ],
        'foundational_stream': [
          { f: 'inventory.feature', n: 'Inventory packaging orchestration guardrails BF MX', c: 1 },
          { f: 'inventory_right_now.feature', n: 'Picking state independence - BF MX', c: 1 },
          { f: 'inventory_right_now_carrier.feature', n: 'Carrier inheritance in Right Now pickings - BF MX', c: 1 },
          { f: 'manufacturing.feature', n: 'Manufacturing guardrails BF MX', c: 1 }
        ],
        'invoice': [
          { f: 'invoice.feature', n: 'Invoice BF MX critical flows', c: 1 }
        ],
        'point_of_sale': [
          { f: 'point_of_sale.feature', n: 'Point of Sale', c: 1 }
        ],
        'recipes_flow': [
          { f: 'Recipes_Flow_MX.feature', n: 'Recipes Flow MX', c: 1 }
        ],
        'refund_chain': [
          { f: 'refund_chain.feature', n: 'BF MX refund chain read-only inspection', c: 1 }
        ],
        'sales_flow': [
          { f: 'Sales_flow.feature', n: 'Sales Flow MX', c: 1 }
        ],
        'smoke': [
          { f: 'smoke.feature', n: 'BF MX staging smoke', c: 1 }
        ]
      },
      'bf-cl': {
        'login': [
          { f: 'login.feature', n: 'Login', c: 1 }
        ],
        'calendar_flow': [
          { f: 'calendar_flow.feature', n: 'Calendar configuration CL', c: 1 }
        ],
        'contacts_flow': [
          { f: 'contacts_flow.feature', n: 'Contacts Flow CL', c: 1 }
        ],
        'smoke': [
          { f: 'smoke.feature', n: 'BF CL staging smoke', c: 1 }
        ]
      },
      'bf-co': {
        'login': [
          { f: 'login.feature', n: 'Login', c: 1 }
        ],
        'calendar_flow': [
          { f: 'calendar_flow.feature', n: 'Calendar configuration CO', c: 1 }
        ],
        'contacts_flow': [
          { f: 'contacts_flow.feature', n: 'Contacts Flow CO', c: 1 }
        ],
        'smoke': [
          { f: 'smoke.feature', n: 'BF CO staging smoke', c: 1 }
        ]
      },
      'bv-mx': {
        'login': [
          { f: 'login.feature', n: 'Login', c: 1 }
        ],
        'calendar_flow': [
          { f: 'calendar_flow.feature', n: 'Calendar configuration BV', c: 1 }
        ],
        'smoke': [
          { f: 'smoke.feature', n: 'BV MX staging smoke', c: 1 }
        ]
      }
    },
    prod: {
      'bf-mx': {
        'accountprod': [
          { f: 'accountprod.feature', n: 'Account', c: 3 }
        ],
        'calendarprod': [
          { f: 'calendarprod.feature', n: 'Calendar MX', c: 4 }
        ],
        'contactsprod': [
          { f: 'contactsprod.feature', n: 'Contacts MX', c: 1 }
        ],
        'loginprod': [
          { f: 'loginprod.feature', n: 'Login', c: 5 }
        ],
        'smokeprod': [
          { f: 'smokeprod.feature', n: 'Smoke Tests (Production)', c: 1 }
        ]
      },
      'bf-cl': {
        'accountprod': [
          { f: 'accountprod.feature', n: 'Account', c: 3 }
        ],
        'calendarprod': [
          { f: 'calendarprod.feature', n: 'Calendar CL', c: 4 }
        ],
        'contactsprod': [
          { f: 'contactsprod.feature', n: 'Contacts CL', c: 1 }
        ],
        'loginprod': [
          { f: 'loginprod.feature', n: 'Login', c: 5 }
        ],
        'smokeprod': [
          { f: 'smokeprod.feature', n: 'Smoke Tests (Production)', c: 1 }
        ]
      },
      'bf-co': {
        'accountprod': [
          { f: 'accountprod.feature', n: 'Account', c: 3 }
        ],
        'calendarprod': [
          { f: 'calendarprod.feature', n: 'Calendar CO', c: 4 }
        ],
        'contactsprod': [
          { f: 'contactsprod.feature', n: 'Contacts CO', c: 1 }
        ],
        'loginprod': [
          { f: 'loginprod.feature', n: 'Login', c: 5 }
        ],
        'smokeprod': [
          { f: 'smokeprod.feature', n: 'Smoke Tests (Production)', c: 1 }
        ]
      },
      'bv-mx': {
        'calendarprod': [
          { f: 'calendarprod.feature', n: 'Calendar BV', c: 4 }
        ],
        'loginprod': [
          { f: 'loginprod.feature', n: 'Login', c: 5 }
        ],
        'smokeprod': [
          { f: 'smokeprod.feature', n: 'Smoke Tests (Production)', c: 1 }
        ]
      }
    },
    dev: {
      'bf-mx': {
        'login': [
          { f: 'login.feature', n: 'Login', c: 1 }
        ],
        'smoke': [
          { f: 'smoke.feature', n: 'Smoke Tests (Development)', c: 1 }
        ]
      },
      'bf-cl': {
        'login': [
          { f: 'login.feature', n: 'Login', c: 1 }
        ]
      },
      'bf-co': {
        'login': [
          { f: 'login.feature', n: 'Login', c: 1 }
        ]
      },
      'bv-mx': {
        'login': [
          { f: 'login.feature', n: 'Login', c: 1 }
        ]
      }
    }
  },
  ecommerce: {
    staging: {
      'bf-mx': {
        'account': [
          { f: 'account.feature', n: 'Account', c: 5 }
        ],
        'appointment': [
          { f: 'appointment.feature', n: 'Appointment', c: 14 }
        ],
        'cart': [
          { f: 'cart.feature', n: 'Cart', c: 13 }
        ],
        'checkout': [
          { f: 'checkout.feature', n: 'Checkout', c: 18 }
        ],
        'configurator': [
          { f: 'configurator.feature', n: 'Configurator', c: 26 }
        ],
        'product_page': [
          { f: 'product_page.feature', n: 'Product Page', c: 58 }
        ],
        'login': [
          { f: 'login.feature', n: 'Login', c: 8 }
        ],
        'auth': [
          { f: 'auth.feature', n: 'Authentication', c: 6 }
        ],
        'search': [
          { f: 'search.feature', n: 'Search', c: 12 }
        ],
        'navigation': [
          { f: 'navigation.feature', n: 'Navigation', c: 10 }
        ],
        'wishlist': [
          { f: 'wishlist.feature', n: 'Wishlist', c: 7 }
        ]
      },
      'bf-cl': {
        'appointment': [
          { f: 'appointment.feature', n: 'Appointment', c: 14 }
        ],
        'cart': [
          { f: 'cart.feature', n: 'Cart', c: 9 }
        ],
        'checkout': [
          { f: 'checkout.feature', n: 'Checkout', c: 16 }
        ],
        'login': [
          { f: 'login.feature', n: 'Login', c: 8 }
        ],
        'auth': [
          { f: 'auth.feature', n: 'Authentication', c: 6 }
        ],
        'search': [
          { f: 'search.feature', n: 'Search', c: 11 }
        ],
        'navigation': [
          { f: 'navigation.feature', n: 'Navigation', c: 9 }
        ]
      },
      'bf-co': {
        'appointment': [
          { f: 'appointment.feature', n: 'Appointment', c: 14 }
        ],
        'checkout': [
          { f: 'checkout.feature', n: 'Checkout', c: 10 }
        ],
        'login': [
          { f: 'login.feature', n: 'Login', c: 8 }
        ],
        'auth': [
          { f: 'auth.feature', n: 'Authentication', c: 6 }
        ],
        'search': [
          { f: 'search.feature', n: 'Search', c: 10 }
        ],
        'navigation': [
          { f: 'navigation.feature', n: 'Navigation', c: 8 }
        ]
      },
      'bv-mx': {
        'appointment': [
          { f: 'appointment.feature', n: 'Appointment', c: 14 }
        ],
        'checkout': [
          { f: 'checkout.feature', n: 'Checkout', c: 11 }
        ],
        'configurator': [
          { f: 'configurator.feature', n: 'Configurator', c: 18 }
        ],
        'login': [
          { f: 'login.feature', n: 'Login', c: 8 }
        ],
        'auth': [
          { f: 'auth.feature', n: 'Authentication', c: 6 }
        ],
        'search': [
          { f: 'search.feature', n: 'Search', c: 10 }
        ],
        'navigation': [
          { f: 'navigation.feature', n: 'Navigation', c: 9 }
        ]
      }
    },
    prod: {
      'bf-mx': {
        'smokeprod': [
          { f: 'smokeprod.feature', n: 'Smoke Tests (Production)', c: 1 }
        ]
      },
      'bf-cl': {
        'smokeprod': [
          { f: 'smokeprod.feature', n: 'Smoke Tests (Production)', c: 1 }
        ]
      },
      'bf-co': {
        'smokeprod': [
          { f: 'smokeprod.feature', n: 'Smoke Tests (Production)', c: 1 }
        ]
      },
      'bv-mx': {
        'smokeprod': [
          { f: 'smokeprod.feature', n: 'Smoke Tests (Production)', c: 1 }
        ]
      }
    },
    dev: {
      'bf-mx': {
        'login': [
          { f: 'login.feature', n: 'Login', c: 1 }
        ]
      },
      'bf-cl': {
        'login': [
          { f: 'login.feature', n: 'Login', c: 1 }
        ]
      },
      'bf-co': {
        'login': [
          { f: 'login.feature', n: 'Login', c: 1 }
        ]
      },
      'bv-mx': {
        'login': [
          { f: 'login.feature', n: 'Login', c: 1 }
        ]
      }
    }
  }
};

// Helper function to get available feature modules for BU and website
export function getAvailableFeatures(
  website: Website,
  buId: string,
  env?: Environment,
): FeatureModule[] {
  // Default to staging if env not provided (safer default)
  const environment: Environment = env || 'staging';
  
  // Special case: Odoo Production only allows smokeprod/*
  if (website === 'odoo' && environment === 'prod') {
    return [{ f: 'smokeprod.feature', n: 'Smoke Tests (Production)', c: 1 }];
  }
  
  // Filter by website → environment → buId
  const buCoverage = COVERAGE[website]?.[environment]?.[buId];
  if (!buCoverage) return [];
  
  // Flatten all modules into feature list
  const features: FeatureModule[] = [];
  for (const moduleName of Object.keys(buCoverage)) {
    const moduleFeatures = buCoverage[moduleName] || [];
    features.push(...moduleFeatures);
  }
  
  return features.sort((a, b) => a.n.localeCompare(b.n));
}

// Helper function to get feature modules (for dropdown)
export function getFeatureModules(website: Website, buId: string, env?: Environment): string[] {
  const environment = env || 'staging';
  const buCoverage = COVERAGE[website]?.[environment]?.[buId];
  if (!buCoverage) return [];
  return Object.keys(buCoverage).sort();
}

// Helper function to format feature value for workflow
export function getFeatureValue(module: string): string {
  // If module name ends with .feature, add wildcard
  return module.endsWith('.feature') ? module.replace('.feature', '/*') : `${module}/*`;
}
